import { alert } from '@/services/alert';
import { formatPhoneNumber } from '@bariendo/lib/phone-validations';
import { useDisclosure } from '@mantine/hooks';
import { ContactPoint } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useEffect, useRef, useState } from 'react';
import { useTimelineContext } from '../pages/PatientTimelinePage/hooks/useTimelineContext';
import { getReferenceString } from '@medplum/core';
import type { SendSmsParams } from '../../../bots/src/handlers/send-sms-message';
import { useFileSelection } from '@/hooks/useFileSelection';
import { uploadAttachments } from '@/services/attachments';
import { fetchBot } from '@/services/services';

export function useSendSmsMessage() {
  const { patientContext, partnerContext, messageBoxContext } = useTimelineContext();

  const patient = patientContext?.patient;
  const organization = partnerContext?.organization;
  const isPartnerContext = !!partnerContext;
  const { setShowMessageInput, autofocusMessageBox, setAutofocusMessageBox, setSentMessage } = messageBoxContext;
  const fileInputRef = useRef<HTMLButtonElement>(null);
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const medplum = useMedplum();
  const { files, handleSelectFiles, removeFile, clearFiles } = useFileSelection();
  const [pdfModalOpened, { open: openPdfModal, close: closePdfModal }] = useDisclosure(false);
  const [selectedPdfFile, setSelectedPdfFile] = useState<File | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const textareaInputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setMessage('');
  }, [patient?.id, organization?.id]);

  useEffect(() => {
    if (!autofocusMessageBox) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const timeoutId = setTimeout(() => {
      textareaInputRef.current?.focus();
      setAutofocusMessageBox(false);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [autofocusMessageBox, setAutofocusMessageBox]);

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSendMessage = async (phoneContact: ContactPoint) => {
    if (!phoneContact) return;
    if (isSending) return;

    try {
      setIsSending(true);
      const attachments = await uploadAttachments(medplum, files);

      // Build bot input - include recipient reference if we know it (partner or patient)
      const botInput: SendSmsParams = {
        phoneNumber: phoneContact.value as string,
        message,
        attachments,
      };

      if (isPartnerContext && organization) {
        botInput.recipient = getReferenceString(organization);
      } else if (patient) {
        botInput.recipient = getReferenceString(patient);
      }

      await fetchBot(medplum, 'send-sms-message', botInput);
      clearFiles();
      setMessage('');
      setShowMessageInput(false);
    } catch (error) {
      alert.error(`Couldn't send SMS: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSending(false);
      setSentMessage(true);
    }
  };

  const handlePdfPreview = (file: File) => {
    setSelectedPdfFile(file);
    const url = URL.createObjectURL(file);
    setPdfUrl(url);
    openPdfModal();
  };

  const handleClosePdfModal = () => {
    closePdfModal();
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    setSelectedPdfFile(null);
  };

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const handleSelectPhone = () => {
    setAutofocusMessageBox(true);
  };

  return {
    textareaInputRef,
    isSending,
    message,
    setMessage,
    handleSendMessage,
    fileInputRef,
    files,
    handleSelectFiles,
    removeFile,
    handleAttachClick,
    handleSelectPhone,
    pdfModalOpened,
    handlePdfPreview,
    handleClosePdfModal,
    selectedPdfFile,
    pdfUrl,
    formatFileSize,
    formatPhoneNumber,
  };
}
